import { useState, useEffect } from 'react';
import { Zap, AlertCircle, CheckCircle, XCircle, Folder, MinusCircle, ArrowLeft } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';

// Declare File System Access API types
declare global {
  interface Window {
    showDirectoryPicker: () => Promise<FileSystemDirectoryHandle>;
  }
}

interface ValidationResult {
  passed: boolean;
  message: string;
  details?: string;
  severity: 'red' | 'orange';
  relevant_files?: string[];
  skipped?: boolean;
  skipReason?: string;
}

interface FixInstructions {
  priority: number;
  category: string;
  steps: string[];
}

interface ValidationResults {
  structure: ValidationResult;
  naming_convention: ValidationResult[];
  clean_presence: ValidationResult;
  titled_presence: ValidationResult;
  count_consistency: ValidationResult;
  afx_project_files: ValidationResult;
  afx_content: ValidationResult;
  audio_presence: ValidationResult;
  audio_format: ValidationResult;
  audio_stems: ValidationResult;
  audio_naming: ValidationResult;
  fixInstructions: FixInstructions[];
}

interface FilmFile {
  name: string;
  path: string;
  type: 'clean' | 'titled-mp4' | 'titled-prores';
}

interface AFXFile {
  name: string;
  path: string;
  extension: string;
}

interface AudioFile {
  name: string;
  path: string;
  extension: string;
}

// =============================================================================
// Validation Logic (Pure TypeScript)
// =============================================================================

/**
 * Validate package directory structure.
 */
function validateStructure(
  hasClean: boolean,
  hasTitled: boolean,
  hasFilmsDir: boolean,
  detectedIssues: string[]
): ValidationResult {
  const issues: string[] = [];
  const suggestions: string[] = [];

  // Check for Films/ wrapper (non-standard)
  if (hasFilmsDir) {
    issues.push('❌ Non-standard structure: Found "Films/" directory wrapper');
    suggestions.push('→ Move contents of Films/Clean/ to Clean/ at package root');
    suggestions.push('→ Move contents of Films/Titled/ to Titled/ at package root');
    suggestions.push('→ Remove the Films/ wrapper directory');
  }

  // Check for missing required directories
  if (!hasClean && !hasFilmsDir) {
    issues.push('❌ Missing required directory: Clean/');
    suggestions.push('→ Create Clean/ directory at package root');
    suggestions.push('→ Place all Clean Master files (.mov or .mxf) in Clean/');
  }

  if (!hasTitled && !hasFilmsDir) {
    issues.push('❌ Missing required directory: Titled/');
    suggestions.push('→ Create Titled/ directory at package root');
    suggestions.push('→ Create Titled/MP4/ subdirectory for .mp4 files');
    suggestions.push('→ Create Titled/ProRes/ subdirectory for .mov files');
  }

  // Add detected issues from directory scanning
  if (detectedIssues.length > 0) {
    issues.push(...detectedIssues.map(i => `❌ ${i}`));
  }

  // Structure validation result
  if (issues.length > 0) {
    const detailsParts = ['**Issues detected:**', ...issues];
    if (suggestions.length > 0) {
      detailsParts.push('', '**How to fix:**', ...suggestions);
    }
    detailsParts.push('', '**Expected structure:**');
    detailsParts.push('  Package/');
    detailsParts.push('  ├── Clean/           (required)');
    detailsParts.push('  ├── Titled/          (required)');
    detailsParts.push('  │   ├── MP4/');
    detailsParts.push('  │   └── ProRes/');
    detailsParts.push('  ├── Audio/           (optional)');
    detailsParts.push('  └── AFX/             (optional)');

    return {
      passed: false,
      message: 'Package structure does not match expected format',
      details: detailsParts.join('\n'),
      severity: 'red',
    };
  }

  // Check for case variations in subdirectories
  const warnings: string[] = [];
  if (detectedIssues.some(i => i.includes('case'))) {
    warnings.push('⚠️  Some directories use non-standard casing (e.g., "mp4" instead of "MP4")');
    warnings.push('   This may cause issues on case-sensitive systems');
  }

  if (warnings.length > 0) {
    return {
      passed: true,
      message: 'Package structure is valid (with minor warnings)',
      details: warnings.join('\n'),
      severity: 'red',
    };
  }

  return {
    passed: true,
    message: 'Package structure follows expected format',
    details: 'All required directories found at correct locations',
    severity: 'red',
  };
}

/**
 * Validate film asset naming convention.
 *
 * Supports both old and new naming formats:
 *
 * Old format (10-11 fields):
 * [CreativeCompany]_[Brand]_[CarlineName]_[CarlineCode]_[CreativeCampaignName]_
 * [AssetCreativeName?]_[Market]_[Master]_[AspectRatio]_[TimeLength]_[Resolution]
 *
 * New format (11-13 fields):
 * [CreativeCompany]_[Brand]_[CarlineName]_[CarlineCode]_[CreativeCampaignName]_
 * [Tier]_[AssetCreativeName?]_[Market]_[Language?]_[Master]_[AspectRatio]_[TimeLength]_[Resolution]
 *
 * New fields:
 * - Tier: Required in new format (e.g., T1, T2, T3, T1-5)
 * - Language: Optional field after Market (e.g., EN, FR, DE)
 */
function validateNamingConvention(filename: string, location: string): ValidationResult | null {
  const nameWithoutExt = filename.replace(/\.(mov|mp4|mxf)$/i, '');

  // Check character count (max 70)
  if (nameWithoutExt.length > 70) {
    return {
      passed: false,
      message: `Filename too long (${location})`,
      details: `Filename has ${nameWithoutExt.length} characters (max: 70). Shorten by abbreviating fields.`,
      severity: 'red',
      relevant_files: [filename],
    };
  }

  // Check for spaces or special characters
  if (nameWithoutExt.includes(' ') || !/^[A-Za-z0-9_-]+$/.test(nameWithoutExt)) {
    return {
      passed: false,
      message: `Invalid characters in filename (${location})`,
      details: 'Filename contains spaces or special characters. Use only alphanumeric, underscores, and hyphens.',
      severity: 'red',
      relevant_files: [filename],
    };
  }

  // Split by underscore
  const segments = nameWithoutExt.split('_');

  // Validate segment count
  // Old format: 10-11 fields (no Tier, no Language)
  // New format: 11-13 fields (with Tier, Language is optional)
  if (segments.length < 10) {
    return {
      passed: false,
      message: `Incomplete filename (${location})`,
      details: `Filename has only ${segments.length} fields (minimum required: 10).\nOld format: [CreativeCompany]_[Brand]_[Carline]_[Code]_[Campaign]_[AssetName]_[Market]_[Master]_[Ratio]_[Duration]_[Resolution]\nNew format: [CreativeCompany]_[Brand]_[Carline]_[Code]_[Campaign]_[Tier]_[AssetName]_[Market]_[Language]_[Master]_[Ratio]_[Duration]_[Resolution]`,
      severity: 'red',
      relevant_files: [filename],
    };
  }

  if (segments.length > 13) {
    return {
      passed: false,
      message: `Filename has too many fields (${location})`,
      details: `Filename has ${segments.length} fields (maximum allowed: 13).\nExpected format: [CreativeCompany]_[Brand]_[Carline]_[Code]_[Campaign]_[Tier]_[AssetName]_[Market]_[Language]_[Master]_[Ratio]_[Duration]_[Resolution]`,
      severity: 'red',
      relevant_files: [filename],
    };
  }

  // Position-specific field validation (working backwards from end)
  // [-1] Resolution: must match pattern like 1920x1080
  if (!/^\d{3,4}x\d{3,4}$/.test(segments[segments.length - 1])) {
    return {
      passed: false,
      message: `Invalid resolution field (${location})`,
      details: `Resolution field '${segments[segments.length - 1]}' is invalid. Expected format: widthxheight (e.g., 1920x1080, 1080x1080). Current value in last position: ${segments[segments.length - 1]}`,
      severity: 'red',
      relevant_files: [filename],
    };
  }

  // [-2] TimeLength: must match pattern like 15s
  if (!/^\d+s$/.test(segments[segments.length - 2])) {
    return {
      passed: false,
      message: `Invalid duration field (${location})`,
      details: `Duration field '${segments[segments.length - 2]}' is invalid. Expected format: number followed by 's' (e.g., 15s, 20s, 30s). Current value in second-to-last position: ${segments[segments.length - 2]}`,
      severity: 'red',
      relevant_files: [filename],
    };
  }

  // [-3] AspectRatio: must be one of the standard formats
  const validAspectRatios = ['1x1', '16x9', '9x16', '4x5', 'TVC', 'Cinema'];
  if (!validAspectRatios.includes(segments[segments.length - 3])) {
    return {
      passed: false,
      message: `Invalid aspect ratio field (${location})`,
      details: `Aspect ratio field '${segments[segments.length - 3]}' is invalid. Expected one of: ${validAspectRatios.join(', ')}. Current value in third-to-last position: ${segments[segments.length - 3]}`,
      severity: 'red',
      relevant_files: [filename],
    };
  }

  // [-4] MasterStatus: must be Clean, Titled, or Ref (case-insensitive)
  const masterStatus = segments[segments.length - 4];
  const masterStatusLower = masterStatus.toLowerCase();
  if (!['clean', 'titled', 'ref'].includes(masterStatusLower)) {
    return {
      passed: false,
      message: `Invalid master status field (${location})`,
      details: `Master status field '${masterStatus}' is invalid. Expected: Clean, Titled, or Ref (case-insensitive). Current value in fourth-to-last position: ${masterStatus}`,
      severity: 'red',
      relevant_files: [filename],
    };
  }

  // Detect format: check if we have Language field (new format with Language)
  // Language is at [-5], Market at [-6] if both are 2-letter codes
  let hasLanguage = false;
  let marketPosition = -5;
  let languagePosition = -5;

  if (
    segments.length >= 6 &&
    /^[A-Z]{2}$/.test(segments[segments.length - 5]) &&
    /^[A-Z]{2}$/.test(segments[segments.length - 6])
  ) {
    // New format with Language: [-6] Market, [-5] Language
    hasLanguage = true;
    marketPosition = -6;
    languagePosition = -5;
  }

  // Validate Market field
  if (!/^[A-Z]{2}$/.test(segments[segments.length + marketPosition])) {
    return {
      passed: false,
      message: `Invalid market code field (${location})`,
      details: `Market code field '${segments[segments.length + marketPosition]}' is invalid.\nExpected: exactly 2 uppercase letters (e.g., EU, UK, US).\nCurrent value at position ${marketPosition}: ${segments[segments.length + marketPosition]}`,
      severity: 'red',
      relevant_files: [filename],
    };
  }

  // Validate Language field if present
  if (hasLanguage) {
    if (!/^[A-Z]{2}$/.test(segments[segments.length + languagePosition])) {
      return {
        passed: false,
        message: `Invalid language code field (${location})`,
        details: `Language code field '${segments[segments.length + languagePosition]}' is invalid.\nExpected: exactly 2 uppercase letters (e.g., EN, FR, DE).\nCurrent value at position ${languagePosition}: ${segments[segments.length + languagePosition]}`,
        severity: 'red',
        relevant_files: [filename],
      };
    }
  }

  // Position-specific field validation (working forward from start)
  // [0] CreativeCompany: 2-3 uppercase letters
  if (!/^[A-Z]{2,3}$/.test(segments[0])) {
    return {
      passed: false,
      message: `Invalid creative company field (${location})`,
      details: `Creative company field '${segments[0]}' is invalid. Expected: 2-3 uppercase letters (e.g., WK, BS, WPP). Current value in first position: ${segments[0]}`,
      severity: 'red',
      relevant_files: [filename],
    };
  }

  // [1] Brand: must be exactly "Ford"
  if (segments[1] !== 'Ford') {
    return {
      passed: false,
      message: `Invalid brand field (${location})`,
      details: `Brand field '${segments[1]}' is invalid. Expected: exactly 'Ford' (case-sensitive). Current value in second position: ${segments[1]}`,
      severity: 'red',
      relevant_files: [filename],
    };
  }

  // Detect and validate Tier field (new format)
  // Tier is at position [5] and follows pattern T1, T2, T1-5, etc.
  // New format: 11-13 fields (with Tier)
  // Old format: 10-11 fields (without Tier)

  // If 12-13 fields, we MUST be in new format (Tier is required)
  if (segments.length >= 12) {
    if (!/^T\d+(-\d+)?$/i.test(segments[5])) {
      return {
        passed: false,
        message: `Invalid tier field (${location})`,
        details: `Tier field '${segments[5]}' is invalid.\nWith ${segments.length} fields, new format is required (Tier field at position 5).\nExpected: T followed by number(s), optionally with dash (e.g., T1, T2, T3, T1-5).\nCurrent value at position 5: ${segments[5]}`,
        severity: 'red',
        relevant_files: [filename],
      };
    }
  }

  // Validate master status matches file location
  // (masterStatus and masterStatusLower already extracted from position [-4] above)
  if (location === 'Clean') {
    if (masterStatusLower !== 'clean') {
      return {
        passed: false,
        message: `Master status mismatch (${location})`,
        details: `File is in Clean directory but filename has '${masterStatus}' status. Expected 'Clean' in master status field (fourth-to-last position).`,
        severity: 'red',
        relevant_files: [filename],
      };
    }
  } else if (location.startsWith('Titled')) {
    if (!['titled', 'ref'].includes(masterStatusLower)) {
      return {
        passed: false,
        message: `Master status mismatch (${location})`,
        details: `File is in Titled directory but filename has '${masterStatus}' status. Expected 'Titled' or 'Ref' in master status field (fourth-to-last position).`,
        severity: 'red',
        relevant_files: [filename],
      };
    }
  }

  // Check for WIP indicators (Titled only)
  if (location.startsWith('Titled')) {
    const wipIndicators = ['WIP', 'wip', 'draft', 'Draft', 'DRAFT'];
    if (wipIndicators.some(indicator => nameWithoutExt.includes(indicator))) {
      return {
        passed: false,
        message: 'Work-in-progress filename',
        details: 'Filename contains WIP/draft indicator. Replace with final approved version.',
        severity: 'red',
        relevant_files: [filename],
      };
    }
  }

  return null; // No issues
}

/**
 * Extract key parameters from filename for matching Clean/Titled files.
 */
function extractFileParameters(filename: string) {
  const nameWithoutExt = filename.replace(/\.(mov|mp4|mxf)$/i, '');

  // Extract aspect ratio (e.g., 1x1, 16x9, 9x16)
  const aspectMatch = nameWithoutExt.match(/(\d+x\d+)/);
  const aspectRatio = aspectMatch ? aspectMatch[1] : null;

  // Extract duration (e.g., 6s, 10s, 15s)
  const durationMatch = nameWithoutExt.match(/(\d+s)/);
  const duration = durationMatch ? durationMatch[1] : null;

  // Extract resolution (e.g., 1920x1080)
  const resolutionMatch = nameWithoutExt.match(/(\d{3,4}x\d{3,4})/);
  const resolution = resolutionMatch ? resolutionMatch[1] : null;

  // Extract market code (e.g., EN, FR, DE)
  const marketMatch = nameWithoutExt.match(/_([A-Z]{2})_/);
  const market = marketMatch ? marketMatch[1] : null;

  // Extract base name (part before Clean/Titled)
  const baseName = nameWithoutExt.replace(/_(Clean|Titled|Ref)_.*$/, '');

  return { aspectRatio, duration, resolution, market, baseName };
}

/**
 * Check if two filenames match on key parameters.
 */
function filesMatch(file1: string, file2: string): boolean {
  const params1 = extractFileParameters(file1);
  const params2 = extractFileParameters(file2);

  return (
    params1.aspectRatio === params2.aspectRatio &&
    params1.duration === params2.duration &&
    params1.resolution === params2.resolution &&
    params1.market === params2.market &&
    params1.baseName.toLowerCase() === params2.baseName.toLowerCase()
  );
}

/**
 * Validate AFX project files presence.
 *
 * Checks for editable project files when motion graphics/3D elements are present.
 *
 * NOTE: The following checks are NOT implemented (require file parsing):
 * - "Not editable" - would require opening project files
 * - "Software too new" - would require parsing file version
 * - "Missing dependencies" - would require parsing project contents
 * - "Failed integrity" - would require file validation
 */
function validateAFXProjectFiles(afxFiles: AFXFile[], hasAFXDir: boolean): ValidationResult {
  if (!hasAFXDir) {
    return {
      passed: true,
      message: 'AFX directory not found (optional)',
      details: 'No AFX directory present - project files check skipped',
      severity: 'red',
    };
  }

  const projectExtensions = ['.prproj', '.aep', '.c4d', '.ma', '.mb', '.nk', '.drp', '.blend'];
  const motionGraphicsExtensions = ['.aep', '.nk'];
  const threeDExtensions = ['.c4d', '.ma', '.mb', '.blend', '.obj', '.fbx', '.usd', '.usda', '.usdc'];

  const projectFiles = afxFiles.filter(f => projectExtensions.includes(f.extension.toLowerCase()));
  const motionGraphicsFiles = afxFiles.filter(f => motionGraphicsExtensions.includes(f.extension.toLowerCase()));
  const threeDFiles = afxFiles.filter(f => threeDExtensions.includes(f.extension.toLowerCase()));

  // Check if motion graphics or 3D elements exist
  const hasMotionGraphics = motionGraphicsFiles.length > 0 ||
    afxFiles.some(f => f.name.toLowerCase().includes('mograph') || f.name.toLowerCase().includes('animation'));
  const has3D = threeDFiles.length > 0 ||
    afxFiles.some(f => f.name.toLowerCase().includes('3d') || f.name.toLowerCase().includes('model'));

  if ((hasMotionGraphics || has3D) && projectFiles.length === 0) {
    const issues: string[] = [];
    if (hasMotionGraphics) issues.push('Motion graphics files detected');
    if (has3D) issues.push('3D files detected');

    return {
      passed: false,
      message: 'Project files missing when motion graphics/3D present',
      details: `${issues.join(', ')} but no editable project files found. Add .aep, .c4d, .ma, .mb, .blend, or .nk files.`,
      severity: 'red',
    };
  }

  if (afxFiles.length > 0 && projectFiles.length === 0) {
    return {
      passed: false,
      message: 'No project files found in AFX directory',
      details: 'AFX directory exists but contains no editable project files (.prproj, .aep, .c4d, .ma, .mb, .nk, .drp, .blend)',
      severity: 'orange',
    };
  }

  // Group by software type
  const softwareMap: Record<string, string> = {
    '.prproj': 'Premiere Pro',
    '.aep': 'After Effects',
    '.c4d': 'Cinema 4D',
    '.ma': 'Maya (ASCII)',
    '.mb': 'Maya (Binary)',
    '.nk': 'Nuke',
    '.drp': 'DaVinci Resolve',
    '.blend': 'Blender',
  };

  const filesBySoftware: Record<string, number> = {};
  for (const proj of projectFiles) {
    const software = softwareMap[proj.extension.toLowerCase()] || 'Unknown';
    filesBySoftware[software] = (filesBySoftware[software] || 0) + 1;
  }

  const detailsList = [`Found ${projectFiles.length} project file(s):`];
  for (const [software, count] of Object.entries(filesBySoftware)) {
    detailsList.push(`  • ${software}: ${count} file(s)`);
  }

  return {
    passed: true,
    message: `Found ${projectFiles.length} project file(s) in AFX directory`,
    details: detailsList.join('\n'),
    severity: 'red',
    relevant_files: projectFiles.map(f => f.name),
  };
}

/**
 * Validate AFX content (separate elements).
 *
 * Checks for pack shots, clean plates, 3D sources, and proper file formats.
 *
 * NOTE: The following checks are NOT implemented (require file parsing/understanding):
 * - "VFX not separated" - would require understanding layer structure
 * - "Animation in Nuke" - would require parsing .nk files
 * - "Wrong fonts" - would require checking font files and project references
 */
function validateAFXContent(afxFiles: AFXFile[], hasAFXDir: boolean): ValidationResult {
  if (!hasAFXDir) {
    return {
      passed: true,
      message: 'AFX directory not found (optional)',
      details: 'No AFX directory present - separate elements check skipped',
      severity: 'red',
    };
  }

  if (afxFiles.length === 0) {
    return {
      passed: false,
      message: 'AFX directory is empty',
      details: 'AFX directory exists but contains no files. Expected separate elements (graphics, logos, pack shots, clean plates, etc.)',
      severity: 'red',
    };
  }

  const issues: string[] = [];
  const warnings: string[] = [];

  // Check for proper formats (images should be high-quality)
  const lowQualityFormats = ['.jpg', '.jpeg', '.gif', '.bmp'];
  const invalidVideoFormats = ['.avi', '.wmv', '.flv'];

  const lowQualityImages = afxFiles.filter(f => lowQualityFormats.includes(f.extension.toLowerCase()));
  const invalidVideos = afxFiles.filter(f => invalidVideoFormats.includes(f.extension.toLowerCase()));

  if (lowQualityImages.length > 0) {
    warnings.push(`⚠️  ${lowQualityImages.length} low-quality image format(s) found (JPG/GIF/BMP)`);
    warnings.push('   → Use PNG, TIFF, PSD, or EXR for better quality');
  }

  if (invalidVideos.length > 0) {
    issues.push(`❌ ${invalidVideos.length} invalid video format(s) found (AVI/WMV/FLV)`);
    issues.push('   → Use MOV, MP4, or MXF formats');
  }

  // Check for pack shots (product images/renders)
  const hasPackShots = afxFiles.some(f =>
    f.name.toLowerCase().includes('pack') ||
    f.name.toLowerCase().includes('product') ||
    f.name.toLowerCase().includes('packshot') ||
    f.name.toLowerCase().includes('pack_shot') ||
    f.name.toLowerCase().includes('package')
  );

  // Check for clean plates (background plates without VFX)
  const hasCleanPlates = afxFiles.some(f =>
    f.name.toLowerCase().includes('clean') ||
    f.name.toLowerCase().includes('plate') ||
    f.name.toLowerCase().includes('bg') ||
    f.name.toLowerCase().includes('background')
  );

  // Check for 3D sources
  const threeDFormats = ['.obj', '.fbx', '.c4d', '.ma', '.mb', '.blend', '.usd', '.usda', '.usdc', '.abc', '.max', '.3ds'];
  const has3DSources = afxFiles.some(f => threeDFormats.includes(f.extension.toLowerCase()));

  const has3DIndicators = afxFiles.some(f =>
    f.name.toLowerCase().includes('3d') ||
    f.name.toLowerCase().includes('model') ||
    f.name.toLowerCase().includes('mesh') ||
    f.name.toLowerCase().includes('geometry')
  );

  // If we have 3D indicators but no 3D source files
  if (has3DIndicators && !has3DSources) {
    issues.push('❌ 3D content detected but no 3D source files found');
    issues.push('   → Add .obj, .fbx, .c4d, .ma, .mb, .blend, or .usd files');
  }

  // Categorize files by type
  const mediaExtensions: Record<string, string[]> = {
    video: ['.mov', '.mp4', '.mxf'],
    'high-quality-image': ['.png', '.tif', '.tiff', '.psd', '.exr', '.dpx'],
    'low-quality-image': ['.jpg', '.jpeg', '.gif', '.bmp'],
    vector: ['.ai', '.eps', '.svg'],
    '3d-source': threeDFormats,
    project: ['.prproj', '.aep', '.c4d', '.ma', '.mb', '.nk', '.drp', '.blend'],
    archive: ['.zip', '.7z', '.rar'],
  };

  const filesByCategory: Record<string, number> = {};
  for (const file of afxFiles) {
    const ext = file.extension.toLowerCase();
    let categorized = false;

    for (const [category, exts] of Object.entries(mediaExtensions)) {
      if (exts.includes(ext)) {
        filesByCategory[category] = (filesByCategory[category] || 0) + 1;
        categorized = true;
        break;
      }
    }

    if (!categorized && ext) {
      filesByCategory['other'] = (filesByCategory['other'] || 0) + 1;
    }
  }

  const detailsList: string[] = [];
  detailsList.push(`Found ${afxFiles.length} file(s) in AFX directory:`);

  for (const [category, count] of Object.entries(filesByCategory).sort()) {
    const displayName = category.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    detailsList.push(`  • ${displayName}: ${count} file(s)`);
  }

  // Add content indicators
  detailsList.push('');
  detailsList.push('Content indicators:');
  detailsList.push(`  • Pack shots: ${hasPackShots ? '✓ Found' : '✗ Not detected'}`);
  detailsList.push(`  • Clean plates: ${hasCleanPlates ? '✓ Found' : '✗ Not detected'}`);
  detailsList.push(`  • 3D sources: ${has3DSources ? '✓ Found' : '✗ Not detected'}`);

  if (warnings.length > 0) {
    detailsList.push('');
    detailsList.push(...warnings);
  }

  if (issues.length > 0) {
    detailsList.push('');
    detailsList.push(...issues);
  }

  const hasCriticalIssues = invalidVideos.length > 0 || (has3DIndicators && !has3DSources);

  return {
    passed: !hasCriticalIssues,
    message: hasCriticalIssues
      ? 'AFX content has format or source file issues'
      : `Found ${afxFiles.length} file(s) in AFX directory`,
    details: detailsList.join('\n'),
    severity: hasCriticalIssues ? 'red' : 'orange',
    relevant_files: hasCriticalIssues ? [...invalidVideos.map(f => f.name)] : undefined,
  };
}

/**
 * Validate audio file presence (when asset includes audio).
 */
function validateAudioPresence(audioFiles: AudioFile[], hasAudioDir: boolean, hasFilmAssets: boolean): ValidationResult {
  if (!hasAudioDir && !hasFilmAssets) {
    // No Audio directory and no film assets - check skipped
    return {
      passed: true,
      message: 'Audio directory not found (optional)',
      details: 'No Audio directory present and no film assets to check - audio check skipped',
      severity: 'orange',
    };
  }

  if (!hasAudioDir && hasFilmAssets) {
    // Film assets exist but no Audio directory - this might be an issue if assets have audio
    return {
      passed: false,
      message: 'No Audio directory found',
      details: 'Film assets are present but no Audio/ directory found. If assets contain audio, add Audio/ directory with separate audio files.',
      severity: 'orange',
    };
  }

  if (hasAudioDir && audioFiles.length === 0) {
    return {
      passed: false,
      message: 'Audio directory is empty',
      details: 'Audio/ directory exists but contains no audio files. Add WAV or AIFF audio files.',
      severity: 'red',
    };
  }

  return {
    passed: true,
    message: `Found ${audioFiles.length} audio file(s)`,
    details: audioFiles.map(f => f.name).join(', '),
    severity: 'red',
  };
}

/**
 * Validate audio file formats (must be WAV or AIFF).
 */
function validateAudioFormat(audioFiles: AudioFile[], hasAudioDir: boolean): ValidationResult {
  if (!hasAudioDir || audioFiles.length === 0) {
    return {
      passed: true,
      message: 'Audio format check skipped',
      details: 'No audio files to validate',
      severity: 'red',
      skipped: true,
      skipReason: 'No audio files present',
    };
  }

  const validExtensions = ['.wav', '.aiff', '.aif'];
  const invalidFiles: string[] = [];

  for (const file of audioFiles) {
    if (!validExtensions.includes(file.extension.toLowerCase())) {
      invalidFiles.push(file.name);
    }
  }

  if (invalidFiles.length > 0) {
    return {
      passed: false,
      message: 'Invalid audio format(s) detected',
      details: `${invalidFiles.length} file(s) are not WAV or AIFF format. All audio files must be .wav or .aiff/.aif format.`,
      severity: 'red',
      relevant_files: invalidFiles,
    };
  }

  return {
    passed: true,
    message: 'All audio files use valid formats',
    details: `${audioFiles.length} file(s) validated as WAV or AIFF`,
    severity: 'red',
  };
}

/**
 * Validate audio stems presence (separate audio tracks like VO, Music, SFX).
 */
function validateAudioStems(audioFiles: AudioFile[], hasAudioDir: boolean): ValidationResult {
  if (!hasAudioDir || audioFiles.length === 0) {
    return {
      passed: true,
      message: 'Audio stems check skipped',
      details: 'No audio files to validate',
      severity: 'red',
      skipped: true,
      skipReason: 'No audio files present',
    };
  }

  // Check for common stem indicators in filenames
  const stemKeywords = ['mix', 'vo', 'music', 'sfx', 'dialogue', 'voice', 'effects', 'stem'];
  const filesWithStems: string[] = [];
  const filesWithoutStems: string[] = [];

  for (const file of audioFiles) {
    const nameLower = file.name.toLowerCase();
    const hasStempIndicator = stemKeywords.some(keyword => nameLower.includes(keyword));

    if (hasStempIndicator) {
      filesWithStems.push(file.name);
    } else {
      filesWithoutStems.push(file.name);
    }
  }

  // Check if we have at least a mix or multiple stems
  const hasMix = filesWithStems.some(f => f.toLowerCase().includes('mix'));
  const hasMultipleStems = filesWithStems.length >= 2;

  if (!hasMix && !hasMultipleStems) {
    return {
      passed: false,
      message: 'Missing audio stems',
      details: 'Expected audio stems (Mix, VO, Music, SFX) not found. Include separate stem files or a Mix file.',
      severity: 'orange',
      relevant_files: filesWithoutStems,
    };
  }

  const detailsList: string[] = [];
  if (hasMix) {
    detailsList.push('✓ Mix file found');
  }
  if (filesWithStems.some(f => f.toLowerCase().includes('vo') || f.toLowerCase().includes('dialogue'))) {
    detailsList.push('✓ Voice/VO stem found');
  }
  if (filesWithStems.some(f => f.toLowerCase().includes('music'))) {
    detailsList.push('✓ Music stem found');
  }
  if (filesWithStems.some(f => f.toLowerCase().includes('sfx') || f.toLowerCase().includes('effects'))) {
    detailsList.push('✓ SFX/Effects stem found');
  }

  return {
    passed: true,
    message: 'Audio stems present',
    details: detailsList.join('\n'),
    severity: 'red',
  };
}

/**
 * Validate audio naming convention (clear and descriptive).
 */
function validateAudioNaming(audioFiles: AudioFile[], hasAudioDir: boolean): ValidationResult {
  if (!hasAudioDir || audioFiles.length === 0) {
    return {
      passed: true,
      message: 'Audio naming check skipped',
      details: 'No audio files to validate',
      severity: 'red',
      skipped: true,
      skipReason: 'No audio files present',
    };
  }

  const unclearNames: string[] = [];
  const validPatterns = [
    /mix/i,
    /vo/i,
    /voice/i,
    /dialogue/i,
    /music/i,
    /sfx/i,
    /effects/i,
    /stem/i,
    /master/i,
  ];

  for (const file of audioFiles) {
    const nameWithoutExt = file.name.replace(/\.(wav|aiff|aif)$/i, '');

    // Check if filename contains descriptive keywords
    const hasDescriptiveKeyword = validPatterns.some(pattern => pattern.test(nameWithoutExt));

    // Check for generic unhelpful names
    const isGeneric = /^(audio|sound|track|file)\d*$/i.test(nameWithoutExt);

    if (!hasDescriptiveKeyword || isGeneric) {
      unclearNames.push(file.name);
    }
  }

  if (unclearNames.length > 0) {
    return {
      passed: false,
      message: 'Unclear audio file naming',
      details: `${unclearNames.length} file(s) have unclear names. Use descriptive names like "Mix", "VO", "Music", "SFX", etc.`,
      severity: 'orange',
      relevant_files: unclearNames,
    };
  }

  return {
    passed: true,
    message: 'All audio files have clear naming',
    details: `${audioFiles.length} file(s) use descriptive naming conventions`,
    severity: 'red',
  };
}

/**
 * Generate fix instructions from failed checks.
 */
function generateFixInstructions(results: Omit<ValidationResults, 'fixInstructions'>): FixInstructions[] {
  const instructions: FixInstructions[] = [];

  // Priority 1: Structure issues (most critical)
  if (!results.structure.passed) {
    const steps: string[] = [];
    if (results.structure.details?.includes('Films/')) {
      steps.push('Remove the "Films/" wrapper directory');
      steps.push('Move all contents from Films/Clean/ to Clean/ at package root');
      steps.push('Move all contents from Films/Titled/ to Titled/ at package root');
    }
    if (results.structure.details?.includes('Clean/') && results.structure.details?.includes('Missing')) {
      steps.push('Create a "Clean/" directory at the package root');
      steps.push('Place all Clean Master files (.mov or .mxf) in Clean/');
    }
    if (results.structure.details?.includes('Titled/') && results.structure.details?.includes('Missing')) {
      steps.push('Create a "Titled/" directory at the package root');
      steps.push('Create "Titled/MP4/" subdirectory for .mp4 files');
      steps.push('Create "Titled/ProRes/" subdirectory for .mov files');
    }
    if (results.structure.details?.includes('lowercase') || results.structure.details?.includes('mp4/')) {
      steps.push('Rename "Titled/mp4/" to "Titled/MP4/" (uppercase)');
    }
    if (results.structure.details?.includes('proRes')) {
      steps.push('Rename "Titled/proRes/" to "Titled/ProRes/" (capital P, capital R)');
    }
    if (results.structure.details?.includes('aspect ratio folder')) {
      steps.push('Remove aspect ratio folders (e.g., "1x1_6s/")');
      steps.push('Move Clean files to Clean/ directory');
      steps.push('Move Titled .mp4 files to Titled/MP4/');
      steps.push('Move Titled .mov files to Titled/ProRes/');
    }
    if (steps.length > 0) {
      instructions.push({
        priority: 1,
        category: '🏗️  Fix Package Structure',
        steps,
      });
    }
  }

  // Priority 2: Missing files
  const missingFileSteps: string[] = [];
  if (!results.clean_presence.passed) {
    missingFileSteps.push('Add Clean Master files (.mov or .mxf) to Clean/ directory');
  }
  if (!results.titled_presence.passed) {
    missingFileSteps.push('Add Titled Master files to Titled/ directory');
    missingFileSteps.push('  → Place .mp4 files in Titled/MP4/');
    missingFileSteps.push('  → Place .mov files in Titled/ProRes/');
  }
  if (missingFileSteps.length > 0) {
    instructions.push({
      priority: 2,
      category: '📁 Add Missing Files',
      steps: missingFileSteps,
    });
  }

  // Priority 3: Naming convention issues
  const namingIssues = results.naming_convention.filter(r => !r.passed);
  if (namingIssues.length > 0) {
    const steps: string[] = [];
    const issueTypes = new Set<string>();

    namingIssues.forEach(issue => {
      if (issue.message.includes('too long')) issueTypes.add('too_long');
      if (issue.message.includes('Invalid characters')) issueTypes.add('invalid_chars');
      if (issue.message.includes('Incomplete')) issueTypes.add('incomplete');
      if (issue.message.includes('Missing') && issue.message.includes('Clean')) issueTypes.add('missing_clean');
      if (issue.message.includes('Missing') && issue.message.includes('Titled')) issueTypes.add('missing_titled');
      if (issue.message.includes('WIP') || issue.message.includes('work-in-progress')) issueTypes.add('wip');
      if (issue.message.includes('duration')) issueTypes.add('missing_duration');
      if (issue.message.includes('resolution')) issueTypes.add('missing_resolution');
    });

    if (issueTypes.has('too_long')) {
      steps.push('Shorten filenames to 70 characters or less');
      steps.push('  → Abbreviate non-critical fields like AssetCreativeName');
    }
    if (issueTypes.has('invalid_chars')) {
      steps.push('Remove spaces and special characters from filenames');
      steps.push('  → Use only letters, numbers, underscores, and hyphens');
    }
    if (issueTypes.has('incomplete')) {
      steps.push('Add all required fields to filenames (minimum 10 fields):');
      steps.push('  → Old format: [Company]_[Brand]_[Carline]_[Code]_[Campaign]_[Asset]_[Market]_[Master]_[Ratio]_[Duration]_[Resolution]');
      steps.push('  → New format: [Company]_[Brand]_[Carline]_[Code]_[Campaign]_[Tier]_[Asset]_[Market]_[Language]_[Master]_[Ratio]_[Duration]_[Resolution]');
    }
    if (issueTypes.has('missing_clean')) {
      steps.push('Add "_Clean_" identifier to Clean Master filenames');
    }
    if (issueTypes.has('missing_titled')) {
      steps.push('Add "_Titled_" or "_Ref_" identifier to Titled Master filenames');
    }
    if (issueTypes.has('wip')) {
      steps.push('Remove WIP/draft indicators from filenames');
      steps.push('  → Replace with final approved version');
    }
    if (issueTypes.has('missing_duration')) {
      steps.push('Add duration field to filenames (e.g., "15s", "20s")');
    }
    if (issueTypes.has('missing_resolution')) {
      steps.push('Add resolution field to filenames (e.g., "1920x1080", "1080x1080")');
    }

    if (steps.length > 0) {
      instructions.push({
        priority: 3,
        category: `📝 Fix ${namingIssues.length} Filename(s)`,
        steps,
      });
    }
  }

  // Priority 4: File pairing issues
  if (!results.count_consistency.passed && !results.count_consistency.skipped) {
    const steps: string[] = [];
    if (results.count_consistency.message.includes('count mismatch')) {
      steps.push('Ensure each Clean Master has a matching Titled Master');
      steps.push('Files must match on: aspect ratio, duration, resolution, and market');
    }
    if (results.count_consistency.relevant_files && results.count_consistency.relevant_files.length > 0) {
      steps.push(`Create Titled Masters for ${results.count_consistency.relevant_files.length} unmatched Clean file(s):`);
      results.count_consistency.relevant_files.forEach(file => {
        steps.push(`  → ${file}`);
      });
    }
    if (steps.length > 0) {
      instructions.push({
        priority: 4,
        category: '🔗 Fix File Pairing',
        steps,
      });
    }
  }

  // Priority 5: AFX issues (optional but recommended)
  const afxSteps: string[] = [];
  if (!results.afx_project_files.passed && !results.afx_project_files.message.includes('optional')) {
    if (results.afx_project_files.message.includes('motion graphics/3D present')) {
      afxSteps.push('Add editable project files for motion graphics/3D content:');
      afxSteps.push('  → After Effects (.aep) for motion graphics');
      afxSteps.push('  → Cinema 4D (.c4d), Maya (.ma/.mb), Blender (.blend) for 3D');
      afxSteps.push('  → Nuke (.nk) for compositing');
    } else {
      afxSteps.push('Add at least one project file to AFX/ directory:');
      afxSteps.push('  → Premiere Pro (.prproj), After Effects (.aep), Cinema 4D (.c4d),');
      afxSteps.push('  → Maya (.ma/.mb), Blender (.blend), Nuke (.nk), or DaVinci Resolve (.drp)');
    }
  }
  if (!results.afx_content.passed && !results.afx_content.message.includes('optional')) {
    if (results.afx_content.message.includes('format') || results.afx_content.details?.includes('❌')) {
      if (results.afx_content.details?.includes('invalid video format')) {
        afxSteps.push('Replace invalid video formats:');
        afxSteps.push('  → Convert AVI/WMV/FLV files to MOV, MP4, or MXF');
      }
      if (results.afx_content.details?.includes('3D content detected')) {
        afxSteps.push('Add 3D source files:');
        afxSteps.push('  → Include .obj, .fbx, .c4d, .ma, .mb, .blend, or .usd files');
      }
    }
    if (results.afx_content.details?.includes('⚠️')) {
      afxSteps.push('Improve image quality:');
      afxSteps.push('  → Replace JPG/GIF/BMP with PNG, TIFF, PSD, or EXR');
    }
    if (results.afx_content.details?.includes('Pack shots: ✗')) {
      afxSteps.push('Consider adding pack shots (product images/renders)');
    }
    if (results.afx_content.details?.includes('Clean plates: ✗')) {
      afxSteps.push('Consider adding clean plates (background plates without VFX)');
    }
    if (!results.afx_content.details?.includes('✗') && !results.afx_content.details?.includes('❌')) {
      afxSteps.push('Add separate elements to AFX/ directory:');
      afxSteps.push('  → Graphics, logos, pack shots, clean plates, 3D sources');
    }
  }
  if (afxSteps.length > 0) {
    instructions.push({
      priority: 5,
      category: '🎨 Fix AFX Content Issues',
      steps: afxSteps,
    });
  }

  // Priority 6: Audio issues
  const audioSteps: string[] = [];
  if (!results.audio_presence.passed && !results.audio_presence.skipped) {
    if (results.audio_presence.message.includes('directory is empty')) {
      audioSteps.push('Add audio files to Audio/ directory');
      audioSteps.push('  → Use WAV or AIFF format only');
    } else if (results.audio_presence.message.includes('No Audio directory found')) {
      audioSteps.push('Create Audio/ directory at package root');
      audioSteps.push('Add separate audio files (Mix, VO, Music, SFX stems)');
    }
  }
  if (!results.audio_format.passed && !results.audio_format.skipped) {
    audioSteps.push('Convert audio files to WAV or AIFF format');
    audioSteps.push('  → Remove MP3, AAC, or other compressed formats');
    if (results.audio_format.relevant_files && results.audio_format.relevant_files.length > 0) {
      audioSteps.push(`Files to convert: ${results.audio_format.relevant_files.slice(0, 3).join(', ')}`);
    }
  }
  if (!results.audio_stems.passed && !results.audio_stems.skipped) {
    audioSteps.push('Add audio stems to Audio/ directory:');
    audioSteps.push('  → Mix (full stereo mix)');
    audioSteps.push('  → VO (voice-over/dialogue)');
    audioSteps.push('  → Music (music track)');
    audioSteps.push('  → SFX (sound effects)');
  }
  if (!results.audio_naming.passed && !results.audio_naming.skipped) {
    audioSteps.push('Rename audio files with descriptive names:');
    audioSteps.push('  → Use "Mix", "VO", "Music", "SFX" in filenames');
    audioSteps.push('  → Avoid generic names like "audio.wav" or "track1.wav"');
  }
  if (audioSteps.length > 0) {
    instructions.push({
      priority: 6,
      category: '🔊 Fix Audio Issues',
      steps: audioSteps,
    });
  }

  return instructions.sort((a, b) => a.priority - b.priority);
}

/**
 * Run all validation checks on collected files with dependency checking.
 */
function runValidation(
  files: FilmFile[],
  afxFiles: AFXFile[],
  audioFiles: AudioFile[],
  hasAFXDir: boolean,
  hasAudioDir: boolean,
  hasClean: boolean,
  hasTitled: boolean,
  hasFilmsWrapper: boolean,
  structureIssues: string[]
): ValidationResults {
  const cleanFiles = files.filter(f => f.type === 'clean');
  const titledMP4Files = files.filter(f => f.type === 'titled-mp4');
  const titledProResFiles = files.filter(f => f.type === 'titled-prores');
  const allTitledFiles = [...titledMP4Files, ...titledProResFiles];

  // STEP 1: Structure Validation (always runs first - no dependencies)
  const structure = validateStructure(hasClean, hasTitled, hasFilmsWrapper, structureIssues);

  // STEP 2: File Presence Checks (depend on structure passing)
  let cleanPresence: ValidationResult;
  let titledPresence: ValidationResult;
  let afxProjectFiles: ValidationResult;
  let afxContent: ValidationResult;

  if (!structure.passed) {
    // Skip presence checks if structure is invalid
    cleanPresence = {
      passed: true,
      message: 'Check skipped',
      details: 'Fix package structure first',
      severity: 'red',
      skipped: true,
      skipReason: 'Package structure must be fixed first',
    };
    titledPresence = {
      passed: true,
      message: 'Check skipped',
      details: 'Fix package structure first',
      severity: 'red',
      skipped: true,
      skipReason: 'Package structure must be fixed first',
    };
    afxProjectFiles = {
      passed: true,
      message: 'Check skipped',
      details: 'Fix package structure first',
      severity: 'red',
      skipped: true,
      skipReason: 'Package structure must be fixed first',
    };
    afxContent = {
      passed: true,
      message: 'Check skipped',
      details: 'Fix package structure first',
      severity: 'red',
      skipped: true,
      skipReason: 'Package structure must be fixed first',
    };
  } else {
    // Structure is valid - run presence checks
    cleanPresence = cleanFiles.length > 0
      ? {
          passed: true,
          message: `Found ${cleanFiles.length} Clean Master file(s)`,
          details: cleanFiles.map(f => f.name).join(', '),
          severity: 'red',
        }
      : {
          passed: false,
          message: 'No Clean Master files found',
          details: 'Expected at least one file in Clean directory (.mov or .mxf)',
          severity: 'red',
        };

    titledPresence = allTitledFiles.length > 0
      ? {
          passed: true,
          message: `Found ${allTitledFiles.length} Titled Master file(s)`,
          details: `MP4: ${titledMP4Files.length}, ProRes: ${titledProResFiles.length}`,
          severity: 'red',
        }
      : {
          passed: false,
          message: 'No Titled Master files found',
          details: 'Expected at least one file in Titled/MP4 (.mp4) or Titled/ProRes (.mov)',
          severity: 'red',
        };

    afxProjectFiles = validateAFXProjectFiles(afxFiles, hasAFXDir);
    afxContent = validateAFXContent(afxFiles, hasAFXDir);
  }

  // STEP 3: Naming Convention (depends on at least one file type existing)
  const namingResults: ValidationResult[] = [];

  if (!structure.passed || (!cleanPresence.passed && !titledPresence.passed)) {
    // Skip naming check if no files or structure invalid
    namingResults.push({
      passed: true,
      message: 'Check skipped',
      details: structure.passed ? 'Add Clean or Titled files first' : 'Fix package structure and add files first',
      severity: 'red',
      skipped: true,
      skipReason: structure.passed ? 'No files to validate' : 'Package structure must be fixed first',
    });
  } else {
    // Run naming validation on all files
    for (const file of files) {
      const location = file.type === 'clean' ? 'Clean' :
                       file.type === 'titled-mp4' ? 'Titled/MP4' : 'Titled/ProRes';
      const result = validateNamingConvention(file.name, location);
      if (result) {
        namingResults.push(result);
      }
    }

    if (namingResults.length === 0) {
      namingResults.push({
        passed: true,
        message: `All ${files.length} film assets follow naming convention`,
        details: 'All filenames validated successfully',
        severity: 'red',
      });
    }
  }

  // STEP 4: File Pairing (depends on both Clean and Titled existing AND naming passing)
  let countConsistency: ValidationResult;
  const namingPassed = namingResults.every(r => r.passed);

  if (!structure.passed || !cleanPresence.passed || !titledPresence.passed) {
    // Skip if structure invalid or files missing
    countConsistency = {
      passed: true,
      message: 'Check skipped',
      details: 'Fix earlier issues first',
      severity: 'red',
      skipped: true,
      skipReason: !structure.passed ? 'Package structure must be fixed first' : 'Both Clean and Titled files required',
    };
  } else if (!namingPassed) {
    // Skip if naming has issues
    countConsistency = {
      passed: true,
      message: 'Check skipped',
      details: 'Fix filename issues first',
      severity: 'red',
      skipped: true,
      skipReason: 'Filenames must follow naming convention',
    };
  } else {
    // All prerequisites met - run pairing check
    const titledCount = Math.max(titledMP4Files.length, titledProResFiles.length);

    if (cleanFiles.length !== titledCount) {
      countConsistency = {
        passed: false,
        message: 'File count mismatch',
        details: `Found ${cleanFiles.length} Clean file(s) but ${titledCount} Titled file(s). Each Clean Master should have a matching Titled file.`,
        severity: 'red',
      };
    } else {
      // Check pairing
      const unmatchedClean: string[] = [];

      for (const cleanFile of cleanFiles) {
        const hasMatch = allTitledFiles.some(titledFile =>
          filesMatch(cleanFile.name, titledFile.name)
        );
        if (!hasMatch) {
          unmatchedClean.push(cleanFile.name);
        }
      }

      if (unmatchedClean.length > 0) {
        countConsistency = {
          passed: false,
          message: 'Missing paired Titled files',
          details: `${unmatchedClean.length} Clean file(s) have no matching Titled file with same duration/aspect ratio/market.`,
          severity: 'red',
          relevant_files: unmatchedClean,
        };
      } else {
        countConsistency = {
          passed: true,
          message: 'All Clean files properly paired with Titled files',
          details: `${cleanFiles.length} Clean file(s) matched with Titled files`,
          severity: 'red',
        };
      }
    }
  }

  // STEP 5: Audio Validation (independent of other checks)
  const hasFilmAssets = files.length > 0;
  const audioPresence = validateAudioPresence(audioFiles, hasAudioDir, hasFilmAssets);
  const audioFormat = validateAudioFormat(audioFiles, hasAudioDir);
  const audioStems = validateAudioStems(audioFiles, hasAudioDir);
  const audioNaming = validateAudioNaming(audioFiles, hasAudioDir);

  // Generate fix instructions
  const resultsForFix = {
    structure,
    naming_convention: namingResults,
    clean_presence: cleanPresence,
    titled_presence: titledPresence,
    count_consistency: countConsistency,
    afx_project_files: afxProjectFiles,
    afx_content: afxContent,
    audio_presence: audioPresence,
    audio_format: audioFormat,
    audio_stems: audioStems,
    audio_naming: audioNaming,
  };
  const fixInstructions = generateFixInstructions(resultsForFix);

  return {
    ...resultsForFix,
    fixInstructions,
  };
}

// =============================================================================
// React Component
// =============================================================================

export const InstantAnalysisView = () => {
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState('');
  const [results, setResults] = useState<ValidationResults | null>(null);
  const [browserSupported, setBrowserSupported] = useState(true);

  // Back navigation path
  const reportsPath = '/reports';

  // Check browser compatibility
  useEffect(() => {
    if (!('showDirectoryPicker' in window)) {
      setBrowserSupported(false);
    }
  }, []);

  const selectFolder = async () => {
    try {
      const dirHandle = await window.showDirectoryPicker();
      await processDirectory(dirHandle);
    } catch (error) {
      if (error instanceof Error && error.name !== 'AbortError') {
        console.error('Error selecting directory:', error);
        alert('Error: ' + error.message);
      }
    }
  };

  const readAFXFilesRecursive = async (
    dirHandle: FileSystemDirectoryHandle,
    currentPath: string,
    afxFiles: AFXFile[]
  ) => {
    for await (const entry of dirHandle.values()) {
      if (entry.kind === 'file') {
        const lastDotIndex = entry.name.lastIndexOf('.');
        const extension = lastDotIndex !== -1 ? entry.name.substring(lastDotIndex) : '';
        afxFiles.push({
          name: entry.name,
          path: `${currentPath}/${entry.name}`,
          extension,
        });
      } else if (entry.kind === 'directory') {
        // Recursively read subdirectories
        await readAFXFilesRecursive(
          entry as FileSystemDirectoryHandle,
          `${currentPath}/${entry.name}`,
          afxFiles
        );
      }
    }
  };

  /**
   * Recursively read video/audio files from a directory.
   * Similar to readAFXFilesRecursive but for specific file extensions.
   */
  const readFilesRecursive = async (
    dirHandle: FileSystemDirectoryHandle,
    currentPath: string,
    extensions: string[],
    files: FilmFile[] | AudioFile[],
    fileType?: 'clean' | 'titled-mp4' | 'titled-prores'
  ) => {
    for await (const entry of dirHandle.values()) {
      if (entry.kind === 'file') {
        const fileName = entry.name.toLowerCase();
        if (extensions.some(ext => fileName.endsWith(ext))) {
          if (fileType) {
            // Video file
            (files as FilmFile[]).push({
              name: entry.name,
              path: `${currentPath}/${entry.name}`,
              type: fileType,
            });
          } else {
            // Audio file
            const lastDotIndex = entry.name.lastIndexOf('.');
            const extension = lastDotIndex !== -1 ? entry.name.substring(lastDotIndex) : '';
            (files as AudioFile[]).push({
              name: entry.name,
              path: `${currentPath}/${entry.name}`,
              extension,
            });
          }
        }
      } else if (entry.kind === 'directory') {
        // Recursively search subdirectories
        await readFilesRecursive(
          entry as FileSystemDirectoryHandle,
          `${currentPath}/${entry.name}`,
          extensions,
          files,
          fileType
        );
      }
    }
  };

  /**
   * Find a directory matching a keyword (case-insensitive).
   * Returns the first directory whose name contains the keyword.
   */
  const findDirectoryByKeyword = async (
    parentHandle: FileSystemDirectoryHandle,
    keyword: string
  ): Promise<{ handle: FileSystemDirectoryHandle; name: string } | null> => {
    for await (const entry of parentHandle.values()) {
      if (entry.kind === 'directory' && entry.name.toLowerCase().includes(keyword)) {
        return { handle: entry as FileSystemDirectoryHandle, name: entry.name };
      }
    }
    return null;
  };

  const processDirectory = async (dirHandle: FileSystemDirectoryHandle) => {
    setIsProcessing(true);
    setProcessingStatus('Reading package structure...');
    setResults(null);

    try {
      const files: FilmFile[] = [];
      const structureIssues: string[] = [];
      let hasCleanDir = false;
      let hasTitledDir = false;
      let hasFilmsWrapper = false;

      // Check for Films/ wrapper (non-standard structure)
      let rootHandle = dirHandle;
      const filmsDir = await findDirectoryByKeyword(dirHandle, 'films');
      if (filmsDir) {
        hasFilmsWrapper = true;
        rootHandle = filmsDir.handle;
        structureIssues.push(`Found "${filmsDir.name}/" wrapper directory - files should be at package root`);
      }

      // Find Clean directory and read files recursively
      const cleanDir = await findDirectoryByKeyword(rootHandle, 'clean');
      if (cleanDir) {
        hasCleanDir = true;
        const cleanPath = hasFilmsWrapper ? `${filmsDir?.name}/${cleanDir.name}` : cleanDir.name;
        await readFilesRecursive(
          cleanDir.handle,
          cleanPath,
          ['.mov', '.mxf'],
          files,
          'clean'
        );
      } else {
        console.log('No Clean directory found');
      }

      // Find Titled directory (matches "title", "titled", "title master", "TITLED_MASTER", etc.)
      const titledDir = await findDirectoryByKeyword(rootHandle, 'title');
      if (titledDir) {
        hasTitledDir = true;
        const titledDirName = titledDir.name;

        // Find MP4 subdirectory and read files recursively
        let foundMP4 = false;
        const mp4Dir = await findDirectoryByKeyword(titledDir.handle, 'mp4');
        if (mp4Dir) {
          foundMP4 = true;
          const mp4Path = hasFilmsWrapper
            ? `${filmsDir?.name}/${titledDirName}/${mp4Dir.name}`
            : `${titledDirName}/${mp4Dir.name}`;
          await readFilesRecursive(
            mp4Dir.handle,
            mp4Path,
            ['.mp4'],
            files,
            'titled-mp4'
          );
        }

        // Find ProRes subdirectory and read files recursively
        let foundProRes = false;
        const proresDir = await findDirectoryByKeyword(titledDir.handle, 'prores');
        if (proresDir) {
          foundProRes = true;
          const proresPath = hasFilmsWrapper
            ? `${filmsDir?.name}/${titledDirName}/${proresDir.name}`
            : `${titledDirName}/${proresDir.name}`;
          await readFilesRecursive(
            proresDir.handle,
            proresPath,
            ['.mov'],
            files,
            'titled-prores'
          );
        }

        // Check if files are mixed directly in Titled/ (non-standard)
        if (!foundMP4 && !foundProRes) {
          for await (const entry of titledDir.handle.values()) {
            if (entry.kind === 'file') {
              if (entry.name.toLowerCase().endsWith('.mp4')) {
                structureIssues.push(`MP4 files found directly in ${titledDirName}/ (should be in ${titledDirName}/MP4/ subdirectory)`);
                files.push({
                  name: entry.name,
                  path: hasFilmsWrapper ? `${filmsDir?.name}/${titledDirName}/${entry.name}` : `${titledDirName}/${entry.name}`,
                  type: 'titled-mp4',
                });
              } else if (entry.name.toLowerCase().endsWith('.mov')) {
                structureIssues.push(`ProRes files found directly in ${titledDirName}/ (should be in ${titledDirName}/ProRes/ subdirectory)`);
                files.push({
                  name: entry.name,
                  path: hasFilmsWrapper ? `${filmsDir?.name}/${titledDirName}/${entry.name}` : `${titledDirName}/${entry.name}`,
                  type: 'titled-prores',
                });
              }
            } else if (entry.kind === 'directory') {
              // Check for aspect ratio folders (e.g., 1x1_6s)
              if (/\d+x\d+/.test(entry.name)) {
                structureIssues.push(`Found aspect ratio folder "${entry.name}/" (files should be in Clean/ and Titled/ instead)`);
                const aspectDir = entry as FileSystemDirectoryHandle;
                for await (const file of aspectDir.values()) {
                  if (file.kind === 'file') {
                    if (file.name.toLowerCase().includes('clean')) {
                      files.push({
                        name: file.name,
                        path: hasFilmsWrapper
                          ? `${filmsDir?.name}/${titledDirName}/${entry.name}/${file.name}`
                          : `${titledDirName}/${entry.name}/${file.name}`,
                        type: 'clean',
                      });
                    } else if (file.name.toLowerCase().endsWith('.mp4')) {
                      files.push({
                        name: file.name,
                        path: hasFilmsWrapper
                          ? `${filmsDir?.name}/${titledDirName}/${entry.name}/${file.name}`
                          : `${titledDirName}/${entry.name}/${file.name}`,
                        type: 'titled-mp4',
                      });
                    } else if (file.name.toLowerCase().endsWith('.mov')) {
                      files.push({
                        name: file.name,
                        path: hasFilmsWrapper
                          ? `${filmsDir?.name}/${titledDirName}/${entry.name}/${file.name}`
                          : `${titledDirName}/${entry.name}/${file.name}`,
                        type: 'titled-prores',
                      });
                    }
                  }
                }
              }
            }
          }
        }
      } else {
        console.log('No Titled directory found');
      }

      // Find AFX directory (matches "afx", "afx_files", etc.)
      const afxFiles: AFXFile[] = [];
      let hasAFXDir = false;
      const afxDir = await findDirectoryByKeyword(dirHandle, 'afx');
      if (afxDir) {
        hasAFXDir = true;
        await readAFXFilesRecursive(afxDir.handle, afxDir.name, afxFiles);
      } else {
        console.log('No AFX directory found');
      }

      // Find Audio directory and read files recursively
      const audioFiles: AudioFile[] = [];
      let hasAudioDir = false;
      const audioDir = await findDirectoryByKeyword(dirHandle, 'audio');
      if (audioDir) {
        hasAudioDir = true;
        await readFilesRecursive(
          audioDir.handle,
          audioDir.name,
          ['.wav', '.aiff', '.aif'],
          audioFiles
        );
      } else {
        console.log('No Audio directory found');
      }

      setProcessingStatus(`Validating ${files.length} film assets...`);

      // Run validation
      const validationResults = runValidation(files, afxFiles, audioFiles, hasAFXDir, hasAudioDir, hasCleanDir, hasTitledDir, hasFilmsWrapper, structureIssues);
      setResults(validationResults);

    } catch (error) {
      console.error('Error processing directory:', error);
      alert('Error processing directory: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setIsProcessing(false);
      setProcessingStatus('');
    }
  };

  const calculateStats = (results: ValidationResults) => {
    const allResults = [
      results.structure,
      ...results.naming_convention,
      results.clean_presence,
      results.titled_presence,
      results.count_consistency,
      results.afx_project_files,
      results.afx_content,
      results.audio_presence,
      results.audio_format,
      results.audio_stems,
      results.audio_naming,
    ];

    const totalChecks = allResults.length;
    const passedChecks = allResults.filter(r => r.passed).length;
    const failedChecks = totalChecks - passedChecks;

    return { totalChecks, passedChecks, failedChecks };
  };

  const checkDefinitions = [
    { id: 'structure', title: 'Package Structure', description: 'Directories organized according to standard format' },
    { id: 'naming_convention', title: 'Naming Convention', description: 'Film asset filenames follow standard format' },
    { id: 'clean_presence', title: 'Clean Masters Present', description: 'At least one Clean Master file exists' },
    { id: 'titled_presence', title: 'Titled Masters Present', description: 'At least one Titled Master file exists' },
    { id: 'count_consistency', title: 'File Count Consistency', description: 'Clean and Titled files properly paired' },
    { id: 'afx_project_files', title: 'AFX Project Files', description: 'Editable project files present in AFX directory' },
    { id: 'afx_content', title: 'AFX Separate Elements', description: 'Graphics, logos, and other elements in AFX' },
    { id: 'audio_presence', title: 'Audio Files Present', description: 'Audio files exist when asset contains audio' },
    { id: 'audio_format', title: 'Audio Format (WAV/AIFF)', description: 'All audio files use WAV or AIFF format' },
    { id: 'audio_stems', title: 'Audio Stems', description: 'Separate audio tracks (Mix, VO, Music, SFX) present' },
    { id: 'audio_naming', title: 'Audio Naming Convention', description: 'Audio files have clear, descriptive names' },
  ];

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-6xl mx-auto">
        {/* Toolbar */}
        <div className="inline-block bg-white rounded-lg shadow-sm border border-gray-200 p-3 mb-6">
          <button
            onClick={() => navigate({ to: reportsPath })}
            className="flex items-center gap-2 px-3 py-2 text-gray-700 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Back</span>
          </button>
        </div>

        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 mb-6">
          <div className="flex items-center gap-3 mb-3">
            <Zap className="w-8 h-8 text-wppBlue" />
            <h1 className="text-4xl font-bold text-gray-900">Instant Package Check</h1>
          </div>
          <p className="text-gray-600 mb-6">
            Quick initial validation of your package structure and file organization.
          </p>

          {/* Checks list */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {checkDefinitions.map((check, index) => (
              <div key={index} className="bg-gray-50 px-4 py-3 rounded-md">
                <div className="font-semibold text-gray-900 text-sm">{check.title}</div>
                <div className="text-xs text-gray-600 mt-1">{check.description}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Main content */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          {/* Info box */}
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6 rounded">
            <h3 className="font-semibold text-blue-900 mb-2 text-sm">How it works</h3>
            <p className="text-sm text-blue-800">
              Use Instant Analysis for a quick first-level check of file structure and naming before submitting for full QC analysis. Select your package directory and we'll validate it instantly.
            </p>
          </div>

          {/* Browser compatibility warning */}
          {!browserSupported && (
            <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 mb-6 rounded">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-yellow-600" />
                <p className="text-sm text-yellow-800">
                  <strong>Browser Compatibility:</strong> This tool requires a Chromium-based browser (Chrome, Edge, Brave) for the File System Access API.
                </p>
              </div>
            </div>
          )}

          {/* Select button */}
          <div className="mb-6">
            <Button
              onClick={selectFolder}
              disabled={isProcessing || !browserSupported}
              size="lg"
              className="w-full md:w-auto"
            >
              <Folder className="w-5 h-5 mr-2" />
              Select Package Directory
            </Button>
          </div>

          {/* Processing state */}
          {isProcessing && (
            <div className="text-center py-8">
              <div className="inline-block w-12 h-12 border-4 border-gray-200 border-t-wppBlue rounded-full animate-spin mb-4"></div>
              <p className="text-gray-600">{processingStatus}</p>
            </div>
          )}

          {/* Results */}
          {results && (
            <div className="mt-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Validation Results</h2>

              {/* Overall status banner */}
              {(() => {
                const stats = calculateStats(results);
                const analysisPasssed = stats.failedChecks === 0;
                return (
                  <div className={`mb-8 p-6 rounded-lg flex items-center gap-4 ${
                    analysisPasssed
                      ? 'bg-gradient-to-r from-green-500 to-green-600'
                      : 'bg-gradient-to-r from-red-500 to-red-600'
                  }`}>
                    {analysisPasssed ? (
                      <CheckCircle className="w-12 h-12 text-white" />
                    ) : (
                      <XCircle className="w-12 h-12 text-white" />
                    )}
                    <div className="text-white">
                      <div className="text-2xl font-bold">
                        {analysisPasssed ? 'Analysis Passed' : 'Analysis Failed'}
                      </div>
                      <div className="text-sm opacity-90">
                        {analysisPasssed
                          ? 'All validation checks passed successfully'
                          : 'One or more validation checks failed'}
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Results by check */}
              <div className="space-y-6">
                {/* Package Structure (Most Critical) */}
                <div className="pt-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4 pb-2 border-b-2 border-wppBlue">
                    Package Structure
                  </h3>
                  <ResultCard result={results.structure} />
                </div>

                {/* Naming Convention */}
                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4 pb-2 border-b-2 border-wppBlue">
                    Naming Convention
                  </h3>
                  <div className="space-y-3">
                    {results.naming_convention.map((result, index) => (
                      <ResultCard key={index} result={result} />
                    ))}
                  </div>
                </div>

                {/* Clean Presence */}
                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4 pb-2 border-b-2 border-wppBlue">
                    Clean Masters
                  </h3>
                  <ResultCard result={results.clean_presence} />
                </div>

                {/* Titled Presence */}
                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4 pb-2 border-b-2 border-wppBlue">
                    Titled Masters
                  </h3>
                  <ResultCard result={results.titled_presence} />
                </div>

                {/* Count Consistency */}
                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4 pb-2 border-b-2 border-wppBlue">
                    File Pairing
                  </h3>
                  <ResultCard result={results.count_consistency} />
                </div>

                {/* AFX Project Files */}
                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4 pb-2 border-b-2 border-wppBlue">
                    AFX Project Files
                  </h3>
                  <ResultCard result={results.afx_project_files} />
                </div>

                {/* AFX Content */}
                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4 pb-2 border-b-2 border-wppBlue">
                    AFX Separate Elements
                  </h3>
                  <ResultCard result={results.afx_content} />
                </div>

                {/* Audio Presence */}
                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4 pb-2 border-b-2 border-wppBlue">
                    Audio Files
                  </h3>
                  <ResultCard result={results.audio_presence} />
                </div>

                {/* Audio Format */}
                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4 pb-2 border-b-2 border-wppBlue">
                    Audio Format
                  </h3>
                  <ResultCard result={results.audio_format} />
                </div>

                {/* Audio Stems */}
                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4 pb-2 border-b-2 border-wppBlue">
                    Audio Stems
                  </h3>
                  <ResultCard result={results.audio_stems} />
                </div>

                {/* Audio Naming */}
                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4 pb-2 border-wppBlue">
                    Audio Naming
                  </h3>
                  <ResultCard result={results.audio_naming} />
                </div>

                {/* Fix Instructions */}
                {results.fixInstructions.length > 0 && (
                  <div className="border-t-4 border-wppBlue mt-8 pt-6">
                    <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                      <AlertCircle className="w-6 h-6 text-wppBlue" />
                      How to Fix These Issues
                    </h2>
                    <p className="text-gray-600 mb-6">
                      Follow these steps in order to resolve all validation issues:
                    </p>

                    <div className="space-y-6">
                      {results.fixInstructions.map((instruction, index) => (
                        <div
                          key={index}
                          className="bg-gradient-to-r from-blue-50 to-white p-6 rounded-lg border-l-4 border-wppBlue"
                        >
                          <h3 className="text-lg font-semibold text-gray-900 mb-3">
                            {instruction.category}
                          </h3>
                          <ul className="space-y-2">
                            {instruction.steps.map((step, stepIndex) => (
                              <li
                                key={stepIndex}
                                className={`text-gray-700 ${
                                  step.startsWith('  →') ? 'ml-6 text-sm text-gray-600' : 'font-medium'
                                }`}
                              >
                                {step}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>

                    <div className="mt-6 p-4 bg-green-50 border-l-4 border-green-500 rounded">
                      <p className="text-sm text-green-800">
                        <strong>💡 Pro tip:</strong> After fixing the issues, run this check again to verify all problems are resolved before uploading for full validation.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Helper component for rendering results
function ResultCard({ result }: { result: ValidationResult }) {
  // Determine card styling based on status
  const isSkipped = result.skipped || false;
  const bgColor = isSkipped
    ? 'bg-gray-50 border-gray-400'
    : result.passed
    ? 'bg-green-50 border-green-500'
    : 'bg-red-50 border-red-500';

  const icon = isSkipped ? (
    <MinusCircle className="w-5 h-5 text-gray-500 mt-0.5 flex-shrink-0" />
  ) : result.passed ? (
    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
  ) : (
    <XCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
  );

  return (
    <div className={`p-4 rounded-lg border-l-4 ${bgColor}`}>
      <div className="flex items-start gap-3">
        {icon}
        <div className="flex-1">
          <h4 className="font-semibold text-gray-900 mb-1">{result.message}</h4>
          {isSkipped && result.skipReason && (
            <div className="text-sm text-gray-600 italic mt-1">
              {result.skipReason}
            </div>
          )}
          {result.details && !isSkipped && (
            <div className="text-sm text-gray-700 whitespace-pre-wrap mt-2 leading-relaxed">
              {result.details}
            </div>
          )}
          {result.relevant_files && result.relevant_files.length > 0 && (
            <div className="mt-3 p-2 bg-gray-100 rounded text-xs font-mono text-gray-700">
              <strong>Files:</strong>{' '}
              {result.relevant_files.slice(0, 5).join(', ')}
              {result.relevant_files.length > 5 && (
                <span className="text-gray-500">
                  {' '}... and {result.relevant_files.length - 5} more
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
